import { useEffect, useState } from "react";
import CSDTable from "@cloudscape-design/components-themed/table";
import {
  Box,
  SpaceBetween,
  Header,
  Spinner,
  CollectionPreferencesProps,
} from "@cloudscape-design/components-themed";
import { COMMON_LABELS, getDivisionLabel } from "../default-constants";
import { LABELS, SLASH } from "./constants";
import { Link } from "react-router-dom";
import { formatDate, formatDateTime } from "../../utils/format";
import { PERMISSIONS } from "../../configs/constants";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import Preference from "../UI/Preference";
import Pagination from "../UI/Pagination";

import {
  useGetApplicationStatusQuery,
  useLazyGetDownloadApplicationQuery,
} from "../../features/api";

import {
  columnDefinitionsBase,
  columnDefinitionsBaseApply,
  columnDefinitionsBaseNotDocomo,
} from "./table-config";
import { useCodelistHook } from "../../app/useCodelistHook";
import { useHasAuthorityHook } from "../../app/useHasAuthorityHook";
import { downloadBase64File } from "../../utils/downLoad";
import { IconButton } from "../UI/button/IconButton";
import {
  changePageSize,
  setColumnWidths,
  setPageIndex,
} from "../../features/slices/application-status-list";

interface TableProps {
  isBackFromDetail: boolean;
  isDocomoUser: boolean;
  isApply: boolean | undefined;
  skip: boolean;
}

/**
 * 申請状況照会一覧 一覧エリア
 */
const Table = (props: TableProps) => {
  const { isBackFromDetail } = props;
  // ユーザ種別
  const hasAuthority = useHasAuthorityHook(PERMISSIONS.MASTER_EDIT);
  const isDocomoUser = props.isDocomoUser;
  const isApply = props.isApply;
  const dispatch = useAppDispatch();
  const { searchCriteria, tableConfig } = useAppSelector(
    (state) => state.applicationStatusList
  );
  // 回答日時は末尾の半角スペースを除去してからリクエストする
  const request = {
    ...searchCriteria,
    fixedTimeStart: searchCriteria.fixedTimeStart.trim(),
    fixedTimeEnd: searchCriteria.fixedTimeEnd.trim(),
  };
  const { pageSize, pageIndex, columnWidths } = tableConfig;
  // spinner・文字列表示有無
  const [isDownloadSpinnerAndStrDisplay, setIsDownloadSpinnerAndStrDisplay] =
    useState<boolean>(false);

  // button表示有無
  const [
    isApplicationStatusDownloadButton,
    setIsApplicationStatusDownloadButton,
  ] = useState<boolean>(false);

  const { applicationType, applicationStatus, applicationDetailStatus } =
    useCodelistHook();

  const { data, isFetching } = useGetApplicationStatusQuery(request, {
    skip: props.skip,
  });

  //申請書ダウンロード
  const [lazyDownloadApplicationTrigger] = useLazyGetDownloadApplicationQuery();

  /**
   * 申請状況詳細ラベルを返却する
   * @param {string[]} appDetailStatusCodes 申請状況詳細コード
   * @returns {string} 申請状況詳細ラベル
   */
  const getApplicationDetailStatus = (appDetailStatusCodes: string[]) => {
    if (!hasAuthority) {
      if (appDetailStatusCodes.includes("01")) {
        return getDivisionLabel(applicationDetailStatus, "01");
      }
      return COMMON_LABELS.HYPHEN;
    }
    const labels = appDetailStatusCodes.map((value) =>
      getDivisionLabel(applicationDetailStatus, value)
    );
    return labels.length === 0 ? COMMON_LABELS.HYPHEN : labels.join("/");
  };

  /**
   * 申請状況ラベルを返却する
   * @param {string[]} appStatusCodes 申請状況コード
   * @returns {string} 申請状況ラベル
   */
  const getApplicationStatus = (appStatusCodes: string[]) => {
    const labels = appStatusCodes.map((value) =>
      getDivisionLabel(applicationStatus, value)
    );
    return labels.length === 0 ? COMMON_LABELS.HYPHEN : labels.join("/");
  };

  /** テーブルカラム定義 */
  const columnItemBaseDocomo = isDocomoUser
    ? columnDefinitionsBase
    : columnDefinitionsBaseNotDocomo;

  const columnItemBase = isApply !== undefined && isApply ? columnDefinitionsBaseApply : columnItemBaseDocomo;

  const columnDefinitions = columnItemBase.map((column, index) => {
    let cell = (e: any): JSX.Element | undefined | string => <></>;

    switch (column.id) {
      case "applicationNo":
        cell = (e: any) => (
          <Link to={`/application-status/${e.applicationNo}`}>
            {e.applicationNo}
          </Link>
        );
        break;
      case "companyName":
        cell = (e: any) => e.companyName;
        break;
      case "merchantsName":
        cell = (e: any) => e.merchantName;
        break;
      case "applicationStatusCodes":
        cell = (e: any) => getApplicationStatus(e.applicationStatusCodes);
        break;
      case "applicationDetailStatusCodes":
        cell = (e: any) =>
          getApplicationDetailStatus(e.applicationDetailStatusCodes);
        break;
      case "applicationDate":
        cell = (e: any) => formatDate(e.applicationDate);
        break;
      case "fixedTime":
        cell = (e: any) =>
          e.fixedTime === null
            ? COMMON_LABELS.HYPHEN
            : formatDateTime(e.fixedTime);
        break;
      case "applicationTypeCode":
        cell = (e: any) =>
          getDivisionLabel(applicationType, e.applicationTypeCode);
        break;
      case "applicationFormType":
        cell = (e: any) => e.applicationFormTypes !== null && e.applicationFormTypes !== undefined
                            && e.applicationFormTypes.length > 0 ? 
                            e.applicationFormTypes.map((it: string) => it === "" ? COMMON_LABELS.HYPHEN : it).join(SLASH) : COMMON_LABELS.HYPHEN;
        break;
      case "serviceName":
        cell = (e: any) => e.serviceName;
        break;
      case "contractType":
        cell = (e: any) => e.contractType;
        break;
      case "contractRoute":
        cell = (e: any) =>
          e.contractType === "直接" ? COMMON_LABELS.HYPHEN : e.contractRoute;
        break;
      case "particularRateFlag":
        cell = (e: any) =>
          e.contractType === "直接"
            ? COMMON_LABELS.HYPHEN
            : e.particularRateFlag;
        break;
    }
    const width = isBackFromDetail ? columnWidths[index] : undefined;

    return { ...column, width, cell };
  });

  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));
  const [pagedItems, setPagedItems] = useState<Object[]>([]);
  const [allItems, setAllItems] = useState<Object[]>([]);

  useEffect(() => {
    if (data !== undefined) {
      setPagedItems(getPagedItems(data, pageIndex, pageSize));
      setAllItems(data);
    }
  }, [data, pageIndex, pageSize]);

  /**
   * ダウンロードボタン押下時の処理
   */
  const downloadApplication = () => {
    // spinner・文字列活性
    setIsDownloadSpinnerAndStrDisplay(true);
    // button活性
    setIsApplicationStatusDownloadButton(true);
    lazyDownloadApplicationTrigger(request)
      .unwrap()
      .then((res) => {
        if (res !== undefined && res.status !== "error") {
          const items = res.items;
          if (items !== null) {
            for (let item of items) {
              // ファイルダウンロード処理実行
              downloadBase64File(item);
            }
          }
        }
      })
      .finally(() => {
        // spinner・文字列非活性
        setIsDownloadSpinnerAndStrDisplay(false);
        // button非活性
        setIsApplicationStatusDownloadButton(false);
      });
  };

  /**
   * テーブルのカラム幅を変更したときの処理.
   * @param {number[]} widths カラム幅リスト
   */
  const onColumnWidthsChange = (widths: number[]) =>
    dispatch(setColumnWidths(widths));

  /**
   * ページ番号を変更したときの処理.
   * @param {number} currentPageIndex 現在のページ番号
   */
  const setCurrentPageIndex = (currentPageIndex: number) =>
    dispatch(setPageIndex(currentPageIndex));

  /**
   * ページサイズを変更したときの処理.
   * @param {CollectionPreferencesProps.Preferences} detail
   */
  const handleOnConfirm = (detail: CollectionPreferencesProps.Preferences) =>
    dispatch(changePageSize(detail.pageSize!));

  return (
    <CSDTable
      columnDefinitions={columnDefinitions}
      loading={isFetching}
      loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
      trackBy="ruleNo"
      items={pagedItems}
      onColumnWidthsChange={({ detail: { widths } }) =>
        onColumnWidthsChange(widths as number[])
      }
      resizableColumns
      sortingDescending
      stickyHeader
      header={
        <SpaceBetween size="xs">
          {" "}
          <Header
            actions={
              isApply !== undefined && isApply ?
              <SpaceBetween direction="horizontal" size="xs">
              {isDownloadSpinnerAndStrDisplay ? (
                <Spinner size="big" />
              ) : (
                <></>
              )}
              {isDownloadSpinnerAndStrDisplay ? (
                <>{COMMON_LABELS.CONTENTS.DL_RUN}</>
              ) : (
                <></>
              )}
              <IconButton
                iconName={"download"}
                label={COMMON_LABELS.BTNS.DL}
                disabled={isApplicationStatusDownloadButton}
                onClick={downloadApplication}
              />
            </SpaceBetween> : <></>
            }
          >
            {LABELS.APPLICATION_STATUS_TITLE}
            {getHeaderCounterText(allItems.length, pagedItems.length)}
          </Header>
        </SpaceBetween>
      }
      pagination={
        <Pagination
          currentPageIndex={pageIndex}
          filteredItems={allItems}
          setCurrentPageIndex={setCurrentPageIndex}
          pageSize={pageSize}
          setPagedItems={setPagedItems}
        />
      }
      preferences={
        <Preference
          preferences={{ pageSize, visibleContent: [] }}
          options={options}
          handleOnConfirm={handleOnConfirm}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          {COMMON_LABELS.CSD.NO_DATA}
        </Box>
      }
    />
  );
};

export default Table;
